/**
 * MKS-372 — hooks for the Time & Material pre-conversion surface.
 *
 * The two project-scoped queries take an `enabled` flag rather than guessing:
 * the endpoints 404 while `mks_372_tm_conversion_check` is off, and the
 * conversion dialog only needs them while it is actually open.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTmConversionReadiness,
  fetchTmConversionSeedOptions,
  applyTmConversionSeed,
  fetchDefaultProjectReadiness,
  type TmConversionSeedInput,
} from '../services/api/tmConversion';
import { invalidateMany } from './queryUtils';

export function useTmConversionReadiness(projectId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['tmConversionReadiness', projectId ?? null],
    queryFn: () => fetchTmConversionReadiness(projectId as number),
    enabled: enabled && typeof projectId === 'number',
  });
}

export function useTmConversionSeedOptions(projectId: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['tmConversionSeedOptions', projectId ?? null],
    queryFn: () => fetchTmConversionSeedOptions(projectId as number),
    enabled: enabled && typeof projectId === 'number',
  });
}

export function useApplyTmConversionSeed(projectId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TmConversionSeedInput) =>
      applyTmConversionSeed(projectId as number, input),
    onSettled: () => {
      // Deliberately NOT `['project', projectId]`: the dialog runs on top of a
      // half-filled project edit form whose hydration effect reloads every
      // field (including the payment type the user is in the middle of
      // changing) the moment that query returns a new object. The form is
      // handed the rates it just created through the dialog's callback
      // instead, and the project row itself has not changed — only its role
      // rates and assignments have.
      invalidateMany(queryClient, [
        ['tmConversionReadiness', projectId ?? null],
        ['tmConversionSeedOptions', projectId ?? null],
        ['projectAssignments', projectId ?? null],
        ['defaultProjectReadiness'],
      ]);
    },
  });
}

export function useDefaultProjectReadiness(enabled: boolean) {
  return useQuery({
    queryKey: ['defaultProjectReadiness'],
    queryFn: fetchDefaultProjectReadiness,
    enabled,
  });
}
